<template>
    <tailwind-layout title="My Payouts">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"> <!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">My Payouts</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="whiteButton" :href="backUrl">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <h1 class="font-semibold text-2xl leading-tight">My Payouts</h1>
                </div>
            </div>

        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch order-1">
                    <div class="sm:w-2/12 pr-4 order-1 sm:order-1 pt-5">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of items per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-3 sm:order-2">
                        <label for="date_from" class="block text-sm font-medium text-gray-700">
                            Date From
                        </label>
                        <input id="date_from" v-model="form.date_from" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-4 sm:order-3">
                        <label for="date_to" class="block text-sm font-medium text-gray-700">
                            Date To
                        </label>
                        <input id="date_to" v-model="form.date_to" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('amount')">
                                                Amount
                                                <icon v-show="form.sort_by === 'amount'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'amount'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Status
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('created_at')">
                                                Payout Initiated At
                                                <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                            </span>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>

                                    <tr v-for="(sweep, index) in sweeps.data" :key="index"
                                           :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer')]"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ sweep.amount }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ sweep.formatted_status }}
                                            <p v-if="sweep.status === 'failed' && sweep.failure_reason" class="text-red-600 text-xs">{{ sweep.failure_reason }}</p>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ sweep.formatted_created_at }}
                                        </td>
                                    </tr>
                                    <tr v-show="!sweeps.data.length">
                                        <td colspan="3" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No payouts were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="sweeps.meta.links" :results="sweeps.meta" :type="'sweeps'"/>
        </div>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'

export default {
    components: {
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon
    },
    props: {
        filters: Object,
        sweeps: Object,
    },
    data() {
        return {
            form: {
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            faChevronLeft,
        }
    },
    computed: {
        backUrl() {
            const urlParams = new URLSearchParams(window.location.search)
            const propertyId = urlParams.get('property_id')
            if (propertyId) {
                return this.route('properties.show', propertyId) + '?tab=rent-payments'
            }
            return this.route('rented-properties.index')
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                const urlParams = new URLSearchParams(window.location.search)
                const propertyId = urlParams.get('property_id')
                let url = this.route('sweeps.index')
                if (propertyId) {
                    url += (url.includes('?') ? '&' : '?') + 'property_id=' + propertyId
                }
                this.$inertia.get(url, pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
